import type { SessionStatus } from '../session/session-provider';

export const publicAnalytics = Object.freeze({
  origin: 'https://events.zeleznalady.cz',
  websiteId: '9026d80f-82a0-4801-b7bd-2e57b04f33f6',
  runtimePath: '/vcode-analytics-tiktok-v1.js',
  integrity: 'sha384-JpAOJexapVVtAZAFpz3dwp4AHY8PLbao7cLk7Mg7VFIDy2g0/bOUl0zb/HO1qbX5',
  collectorPath: '/analytics/public/v2/events',
  allowedPaths: ['/'] as readonly string[],
  allowedEvents: [] as readonly string[],
});

interface PublicAnalyticsClient {
  pageview(path: string): void;
}

export interface PublicAnalyticsRuntime {
  readonly contractVersion: 'vcode-public-v2';
  create(config: {
    websiteId: string;
    collectorPath: string;
    allowedPaths: readonly string[];
    allowedEvents: readonly string[];
    captureSources: true;
    autoPageview: false;
    autoClick: false;
    captureTitle: false;
    captureReferrer: false;
    credentials: 'omit';
    offline: 'discard';
  }): PublicAnalyticsClient;
}

export interface PublicLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export interface PublicAnalyticsEnvironment {
  read(): PublicLocation & {
    readonly origin: string;
    readonly doNotTrack: boolean;
    readonly globalPrivacyControl: boolean;
    readonly online: boolean;
  };
  loadRuntime(): Promise<PublicAnalyticsRuntime>;
}

function publicLocation(location: PublicLocation): boolean {
  return location.pathname === '/' && location.search === '' && location.hash === '';
}

/** One public pageview per document. Entering another area permanently pauses it. */
export function createPublicAnalyticsBridge(environment: PublicAnalyticsEnvironment) {
  let status: SessionStatus = 'loading';
  let route: PublicLocation | undefined;
  let paused = false;
  let started = false;
  let sent = false;

  function canMeasure(): boolean {
    const current = environment.read();
    return !paused && status === 'unauthenticated' && !!route && publicLocation(route)
      && current.origin === publicAnalytics.origin && publicLocation(current)
      && !current.doNotTrack && !current.globalPrivacyControl && current.online;
  }

  return Object.freeze({
    observe(nextStatus: SessionStatus, nextRoute: PublicLocation): void {
      status = nextStatus;
      route = nextRoute;
      const current = environment.read();
      if (!publicLocation(nextRoute) || !publicLocation(current)
        || current.origin !== publicAnalytics.origin
        || nextStatus === 'authenticated' || nextStatus === 'error'
        || (started && nextStatus !== 'unauthenticated')) {
        paused = true;
      }
      if (started || sent || !canMeasure()) return;
      started = true;
      void environment.loadRuntime().then((runtime) => {
        // A route/auth/privacy change during the lazy download must discard the view.
        if (sent || !canMeasure() || runtime.contractVersion !== 'vcode-public-v2') return;
        const client = runtime.create({
          websiteId: publicAnalytics.websiteId,
          collectorPath: publicAnalytics.collectorPath,
          allowedPaths: publicAnalytics.allowedPaths,
          allowedEvents: publicAnalytics.allowedEvents,
          captureSources: true,
          autoPageview: false,
          autoClick: false,
          captureTitle: false,
          captureReferrer: false,
          credentials: 'omit',
          offline: 'discard',
        });
        if (!canMeasure()) return;
        sent = true;
        client.pageview('/');
      }).catch(() => {
        // Optional analytics never blocks navigation and never queues a retry.
      });
    },
  });
}

declare global {
  interface Window {
    vcodePublicAnalytics?: PublicAnalyticsRuntime;
  }
}

export function loadPublicAnalyticsRuntime(): Promise<PublicAnalyticsRuntime> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = publicAnalytics.runtimePath;
    script.integrity = publicAnalytics.integrity;
    script.crossOrigin = 'anonymous';
    script.referrerPolicy = 'no-referrer';
    script.async = true;
    script.addEventListener('load', () => {
      const runtime = window.vcodePublicAnalytics;
      if (runtime?.contractVersion === 'vcode-public-v2') resolve(runtime);
      else reject(new Error('Unsupported public analytics runtime'));
    }, { once: true });
    script.addEventListener('error', () => reject(new Error('Public analytics unavailable')), { once: true });
    document.head.append(script);
  });
}

export const publicAnalyticsBridge = createPublicAnalyticsBridge({
  read: () => ({
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    doNotTrack: navigator.doNotTrack === '1'
      || (window as Window & { doNotTrack?: string }).doNotTrack === '1',
    globalPrivacyControl: (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true,
    online: navigator.onLine !== false,
  }),
  loadRuntime: loadPublicAnalyticsRuntime,
});
